import ReactSelect from 'react-select';
import { Filter, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import type { DateRange } from '../../helpers/get-current-test-period-range';

const MAX_TEST_FILTER_DAYS = 31;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

interface TeamOption {
  value: number;
  label: string;
}

interface TestsFilterBarProps {
  draft: DateRange;
  applied: DateRange;
  onDraftChange: (draft: DateRange) => void;
  onApply: () => void;
  onReset: () => void;
  teamOptions: TeamOption[];
  selectedTeamIds: number[];
  onTeamsChange: (teamIds: number[]) => void;
}

function countDaysInclusive(range: DateRange): number {
  const start = Date.parse(`${range.startDate}T00:00:00Z`);
  const end = Date.parse(`${range.endDate}T00:00:00Z`);
  return (end - start) / MILLISECONDS_PER_DAY + 1;
}

function getDateRangeError(range: DateRange): string | null {
  if (!range.startDate || !range.endDate) return null;
  const days = countDaysInclusive(range);
  if (days < 1) return 'End date must be on or after the start date';
  if (days > MAX_TEST_FILTER_DAYS) {
    return `Pick a range of ${MAX_TEST_FILTER_DAYS} days or less`;
  }
  return null;
}

function formatRangeLabel(range: DateRange): string {
  const start = format(parseISO(range.startDate), 'd MMM yyyy');
  const end = format(parseISO(range.endDate), 'd MMM yyyy');
  return `${start} to ${end}`;
}

export function TestsFilterBar({
  draft,
  applied,
  onDraftChange,
  onApply,
  onReset,
  teamOptions,
  selectedTeamIds,
  onTeamsChange,
}: TestsFilterBarProps) {
  const error = getDateRangeError(draft);
  const isIncomplete = !draft.startDate || !draft.endDate;

  return (
    <div className="space-y-2 mb-6">
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="tests-start-date">
          Start date
        </label>
        <input
          id="tests-start-date"
          type="date"
          value={draft.startDate}
          onChange={(event) =>
            onDraftChange({ ...draft, startDate: event.target.value })
          }
          className="h-8 px-2 text-xs border rounded-md bg-background"
        />
        <span className="text-xs text-muted-foreground">to</span>
        <label className="sr-only" htmlFor="tests-end-date">
          End date
        </label>
        <input
          id="tests-end-date"
          type="date"
          value={draft.endDate}
          onChange={(event) =>
            onDraftChange({ ...draft, endDate: event.target.value })
          }
          className="h-8 px-2 text-xs border rounded-md bg-background"
        />
        <button
          disabled={isIncomplete || error !== null}
          onClick={onApply}
          className="h-8 px-3 text-xs font-medium rounded-md bg-accent-purple text-white hover:bg-accent-purple/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          <Filter className="h-3 w-3" />
          Filter
        </button>
        <button
          onClick={onReset}
          aria-label="Reset dates to the current period"
          className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-accent/10 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <span className="ml-auto text-xs text-muted-foreground">
          {formatRangeLabel(applied)}
        </span>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      <div className="inline-block min-w-[220px] max-w-full">
        <ReactSelect
          isMulti
          options={teamOptions}
          value={teamOptions.filter((option) =>
            selectedTeamIds.includes(option.value),
          )}
          onChange={(selectedOptions) =>
            onTeamsChange((selectedOptions ?? []).map((option) => option.value))
          }
          placeholder="All teams"
        />
      </div>
    </div>
  );
}
